import {
  Alert,
  Box,
  Button,
  Card,
  Heading,
  Input,
  NativeSelect,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  CheckboxMultiSelect,
  type CheckboxMultiSelectOption,
  Field,
} from '@mangovoice/mango-ui'

import type { UserGroupApi } from '../api/userGroupApi'
import type { PbxUserItem } from '../api/userGroupApi'
import { usePbxUsers } from '../hooks/useUserGroups'
import { UserGroupsI18nProvider, useUserGroupsI18n } from '../i18n'
import type { UserGroupsTranslationLike } from '../i18n'
import type {
  UserGroup,
  UserGroupCreate,
  UserGroupUpdate,
} from '../types/userGroup.types'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UserGroupFormState {
  name: string
  internal_access: number
  local_access: number
  domestic_long_distance_access: number
  international_access: number
  toll_numbers_access: number
  malicious_area_codes_access: number
  super_access: number
  eavesdropping_access: number
  prompt_recording_access: number
  selectedUserIds: Set<number>
}

export interface UserGroupFormProps {
  api: UserGroupApi
  pbxId: number
  mode: 'add' | 'edit'
  userGroup?: UserGroup | null
  isSubmitting: boolean
  onSubmit: (data: UserGroupCreate | UserGroupUpdate) => void
  onCancel: () => void
  canEditAccessFields?: boolean
  t?: UserGroupsTranslationLike
  locale?: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ACCESS_OPTIONS_THREE = [
  { value: '1', labelKey: 'userGroups.common.yes', fallback: 'Yes' },
  { value: '0', labelKey: 'userGroups.common.no', fallback: 'No' },
  {
    value: '2',
    labelKey: 'userGroups.common.requirePin',
    fallback: 'Require PIN',
  },
] as const

const ACCESS_OPTIONS_TWO = [
  { value: '1', labelKey: 'userGroups.common.yes', fallback: 'Yes' },
  { value: '0', labelKey: 'userGroups.common.no', fallback: 'No' },
] as const

function getDefaultFormState(): UserGroupFormState {
  return {
    name: '',
    internal_access: 0,
    local_access: 0,
    domestic_long_distance_access: 0,
    international_access: 0,
    toll_numbers_access: 0,
    malicious_area_codes_access: 0,
    super_access: 0,
    eavesdropping_access: 0,
    prompt_recording_access: 0,
    selectedUserIds: new Set(),
  }
}

function userGroupToFormState(group: UserGroup): UserGroupFormState {
  return {
    name: group.name,
    internal_access: group.internal_access,
    local_access: group.local_access,
    domestic_long_distance_access: group.domestic_long_distance_access,
    international_access: group.international_access,
    toll_numbers_access: group.toll_numbers_access,
    malicious_area_codes_access: group.malicious_area_codes_access,
    super_access: group.super_access,
    eavesdropping_access: group.eavesdropping_access,
    prompt_recording_access: group.prompt_recording_access,
    selectedUserIds: new Set(group.users),
  }
}

function formStateToCreatePayload(state: UserGroupFormState): UserGroupCreate {
  return {
    name: state.name.trim(),
    internal_access: state.internal_access,
    local_access: state.local_access,
    domestic_long_distance_access: state.domestic_long_distance_access,
    international_access: state.international_access,
    toll_numbers_access: state.toll_numbers_access,
    malicious_area_codes_access: state.malicious_area_codes_access,
    super_access: state.super_access,
    eavesdropping_access: state.eavesdropping_access,
    prompt_recording_access: state.prompt_recording_access,
    users: Array.from(state.selectedUserIds),
  }
}

function formStateToUpdatePayload(
  state: UserGroupFormState,
  canEditAccessFields: boolean,
): UserGroupUpdate {
  const base: UserGroupUpdate = {
    name: state.name.trim(),
    users: Array.from(state.selectedUserIds),
  }

  if (!canEditAccessFields) {
    return base
  }

  return {
    ...base,
    internal_access: state.internal_access,
    local_access: state.local_access,
    domestic_long_distance_access: state.domestic_long_distance_access,
    international_access: state.international_access,
    toll_numbers_access: state.toll_numbers_access,
    malicious_area_codes_access: state.malicious_area_codes_access,
    super_access: state.super_access,
    eavesdropping_access: state.eavesdropping_access,
    prompt_recording_access: state.prompt_recording_access,
  }
}

const getUserDisplayName = (user: PbxUserItem['user']): string =>
  user.name || `${user.first_name} ${user.last_name}`.trim()

const detailsCardStyles = {
  p: 4,
  bg: 'bg.default',
  borderRadius: 'lg',
  boxShadow:
    '0px 2px 4px rgba(24, 24, 27, 0.10), 0px 0px 1px rgba(24, 24, 27, 0.30)',
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface AccessSelectProps {
  label: string
  value: number
  onChange: (value: number) => void
  options: 'three' | 'two'
  disabled?: boolean
}

const AccessSelect: React.FC<AccessSelectProps> = ({
  label,
  value,
  onChange,
  options,
  disabled = false,
}) => {
  const { text } = useUserGroupsI18n()
  const items = options === 'three' ? ACCESS_OPTIONS_THREE : ACCESS_OPTIONS_TWO
  return (
    <Field.Root gap={1.5}>
      <Field.Label fontSize="sm" fontWeight="semibold" color="fg.default">
        {label}
      </Field.Label>
      <NativeSelect.Root size="md" disabled={disabled}>
        <NativeSelect.Field
          value={value.toString()}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            onChange(parseInt(e.target.value, 10))
          }
          h="40px"
          px={3}
          bg="bg.default"
          borderColor="border.default"
          borderRadius="md"
          color="fg.muted"
          _hover={{ borderColor: 'border.emphasized' }}
          _focusVisible={{
            borderColor: 'brand.solid',
            boxShadow: '0 0 0 2px var(--chakra-colors-brand-solid)',
            outline: 'none',
          }}
        >
          {items.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {text(opt.labelKey, opt.fallback)}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    </Field.Root>
  )
}

interface MemberPickerProps {
  pbxUsers: PbxUserItem[]
  selectedUserIds: Set<number>
  onToggle: (userId: number) => void
  disabled?: boolean
  isLoading: boolean
}

const MemberPicker: React.FC<MemberPickerProps> = ({
  pbxUsers,
  selectedUserIds,
  onToggle,
  disabled = false,
  isLoading,
}) => {
  const { text } = useUserGroupsI18n()
  const options = useMemo<CheckboxMultiSelectOption[]>(
    () =>
      pbxUsers.map(({ user }) => ({
        id: user.id,
        label: getUserDisplayName(user),
        secondaryLabel: user.email ?? undefined,
        searchText: [user.username ?? '', user.email ?? ''].filter(Boolean),
      })),
    [pbxUsers],
  )

  return (
    <CheckboxMultiSelect
      options={options}
      selectedIds={selectedUserIds}
      onToggle={(id) => onToggle(id as number)}
      disabled={disabled}
      isLoading={isLoading}
      placeholder={text(
        'userGroups.form.memberPicker.placeholder',
        'Select members...',
      )}
      searchPlaceholder={text(
        'userGroups.form.memberPicker.searchPlaceholder',
        'Filter members...',
      )}
      emptyLabel={text(
        'userGroups.form.memberPicker.emptyLabel',
        'No users assigned to this PBX.',
      )}
      noResultsLabel={text(
        'userGroups.form.memberPicker.noResultsLabel',
        'No users match your filter.',
      )}
      renderSummary={(count) =>
        count === 1
          ? text(
              'userGroups.form.memberPicker.summary_one',
              '{{count}} member selected',
              { count },
            )
          : text(
              'userGroups.form.memberPicker.summary_other',
              '{{count}} members selected',
              { count },
            )
      }
    />
  )
}

interface FormSectionCardProps {
  title: string
  children: React.ReactNode
}

const FormSectionCard: React.FC<FormSectionCardProps> = ({
  title,
  children,
}) => (
  <Box {...detailsCardStyles}>
    <VStack align="stretch" gap={4}>
      <Heading as="h2" fontSize="md" fontWeight="semibold">
        {title}
      </Heading>
      {children}
    </VStack>
  </Box>
)

// ─── Main component ───────────────────────────────────────────────────────────

export const UserGroupForm: React.FC<UserGroupFormProps> = ({
  t,
  locale,
  ...props
}) => (
  <UserGroupsI18nProvider t={t} locale={locale}>
    <UserGroupFormInner {...props} />
  </UserGroupsI18nProvider>
)

type UserGroupFormInnerProps = Omit<UserGroupFormProps, 't' | 'locale'>

const UserGroupFormInner: React.FC<UserGroupFormInnerProps> = ({
  api,
  pbxId,
  mode,
  userGroup,
  isSubmitting,
  onSubmit,
  onCancel,
  canEditAccessFields = true,
}) => {
  const { text } = useUserGroupsI18n()
  const isEdit = mode === 'edit'
  const isAutoGenerated = isEdit && userGroup?.auto_generated === true
  const disableMembers = isAutoGenerated
  const hideSubmit = isAutoGenerated && !canEditAccessFields

  const [formState, setFormState] = useState<UserGroupFormState>(() =>
    isEdit && userGroup
      ? userGroupToFormState(userGroup)
      : getDefaultFormState(),
  )
  const [nameError, setNameError] = useState<string | null>(null)

  // Reset form when userGroup data loads/changes in edit mode
  useEffect(() => {
    if (isEdit && userGroup) {
      setFormState(userGroupToFormState(userGroup))
      setNameError(null)
    }
  }, [isEdit, userGroup])

  const { pbxUsers, isLoading: isLoadingUsers } = usePbxUsers({
    api,
    pbxId,
    queryOptions: { enabled: pbxId > 0 },
  })

  const updateField = useCallback(
    <K extends keyof UserGroupFormState>(
      field: K,
      value: UserGroupFormState[K],
    ) => {
      setFormState((prev) => ({ ...prev, [field]: value }))
      if (field === 'name') setNameError(null)
    },
    [],
  )

  const handleToggleUser = useCallback((userId: number) => {
    setFormState((prev) => {
      const next = new Set(prev.selectedUserIds)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return { ...prev, selectedUserIds: next }
    })
  }, [])

  const handleSubmit = useCallback(() => {
    const trimmedName = formState.name.trim()
    if (!trimmedName) {
      setNameError(text('userGroups.form.nameRequired', 'Name is required.'))
      return
    }
    if (trimmedName.length > 64) {
      setNameError(
        text(
          'userGroups.form.nameMaxLength',
          'Name must be 64 characters or less.',
        ),
      )
      return
    }

    if (isEdit) {
      onSubmit(formStateToUpdatePayload(formState, canEditAccessFields))
    } else {
      onSubmit(formStateToCreatePayload(formState))
    }
  }, [formState, isEdit, onSubmit, canEditAccessFields, text])

  const canSubmit = formState.name.trim().length > 0 && !isSubmitting

  return (
    <Card.Root>
      <Card.Header p={6}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Heading as="h1" fontSize="xl" fontWeight="semibold">
            {isEdit
              ? text('userGroups.form.editTitle', 'Edit User Group')
              : text('userGroups.form.addTitle', 'Add User Group')}
          </Heading>
          {isAutoGenerated && (
            <Alert.Root status="info" size="sm" w="auto">
              <Alert.Indicator />
              <Alert.Description fontSize="sm">
                {text(
                  'userGroups.form.autoGeneratedNotice',
                  'Auto-generated group - some fields are restricted.',
                )}
              </Alert.Description>
            </Alert.Root>
          )}
        </Box>
      </Card.Header>

      <Card.Body p={6}>
        <VStack align="stretch" gap={6}>
          {/* Name */}
          <Field.Root invalid={!!nameError} required>
            <Field.Label>
              {text('userGroups.form.nameLabel', 'Name')}
            </Field.Label>
            <Input
              value={formState.name}
              onChange={(e) => updateField('name', e.target.value)}
              maxLength={64}
              disabled={isAutoGenerated}
              placeholder={text(
                'userGroups.form.namePlaceholder',
                'Enter group name',
              )}
              bg="bg.subtle"
              borderColor="border.default"
              _hover={{ borderColor: 'border.emphasized' }}
              _focusVisible={{
                borderColor: 'brand.solid',
                boxShadow: '0 0 0 2px var(--chakra-colors-brand-solid)',
                outline: 'none',
              }}
            />
            {nameError && <Field.ErrorText>{nameError}</Field.ErrorText>}
          </Field.Root>

          <Box
            display="grid"
            gridTemplateColumns={{
              base: '1fr',
              lg: canEditAccessFields ? 'repeat(2, minmax(0, 1fr))' : '1fr',
            }}
            gap={6}
            alignItems="start"
          >
            {canEditAccessFields && (
              <FormSectionCard
                title={text(
                  'userGroups.form.accessPermissions',
                  'Access Permissions',
                )}
              >
                <VStack align="stretch" gap={2}>
                  <AccessSelect
                    label={text('userGroups.form.access.internal', 'Internal')}
                    value={formState.internal_access}
                    onChange={(v) => updateField('internal_access', v)}
                    options="three"
                  />
                  <AccessSelect
                    label={text('userGroups.form.access.local', 'Local')}
                    value={formState.local_access}
                    onChange={(v) => updateField('local_access', v)}
                    options="three"
                  />
                  <AccessSelect
                    label={text(
                      'userGroups.form.access.domesticLongDistance',
                      'Domestic Long Distance',
                    )}
                    value={formState.domestic_long_distance_access}
                    onChange={(v) =>
                      updateField('domestic_long_distance_access', v)
                    }
                    options="three"
                  />
                  <AccessSelect
                    label={text(
                      'userGroups.form.access.international',
                      'International',
                    )}
                    value={formState.international_access}
                    onChange={(v) => updateField('international_access', v)}
                    options="three"
                  />
                  <AccessSelect
                    label={text(
                      'userGroups.form.access.tollNumbers',
                      'Toll Numbers',
                    )}
                    value={formState.toll_numbers_access}
                    onChange={(v) => updateField('toll_numbers_access', v)}
                    options="three"
                  />
                  <AccessSelect
                    label={text(
                      'userGroups.form.access.maliciousAreaCodes',
                      'Malicious Area Codes',
                    )}
                    value={formState.malicious_area_codes_access}
                    onChange={(v) =>
                      updateField('malicious_area_codes_access', v)
                    }
                    options="two"
                  />
                  <AccessSelect
                    label={text(
                      'userGroups.form.access.superAccess',
                      'Super Access',
                    )}
                    value={formState.super_access}
                    onChange={(v) => updateField('super_access', v)}
                    options="two"
                  />
                  <AccessSelect
                    label={text(
                      'userGroups.form.access.eavesdropping',
                      'Eavesdropping',
                    )}
                    value={formState.eavesdropping_access}
                    onChange={(v) => updateField('eavesdropping_access', v)}
                    options="two"
                  />
                  <AccessSelect
                    label={text(
                      'userGroups.form.access.promptRecording',
                      'Prompt Recording',
                    )}
                    value={formState.prompt_recording_access}
                    onChange={(v) => updateField('prompt_recording_access', v)}
                    options="two"
                  />
                </VStack>
              </FormSectionCard>
            )}

            <FormSectionCard title={text('userGroups.form.members', 'Members')}>
              <MemberPicker
                pbxUsers={pbxUsers}
                selectedUserIds={formState.selectedUserIds}
                onToggle={handleToggleUser}
                isLoading={isLoadingUsers}
                disabled={disableMembers}
              />
            </FormSectionCard>
          </Box>
        </VStack>
      </Card.Body>

      {/* Footer with actions */}
      <Box
        p={6}
        borderTopWidth={1}
        borderColor="border.default"
        display="flex"
        justifyContent="flex-end"
        gap={3}
      >
        <Button
          variant="outline"
          colorPalette="neutral"
          onClick={onCancel}
          disabled={isSubmitting}
          px={5}
        >
          {hideSubmit
            ? text('userGroups.common.close', 'Close')
            : text('userGroups.common.cancel', 'Cancel')}
        </Button>
        {!hideSubmit && (
          <Button
            colorPalette="brand"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            px={5}
          >
            {isEdit
              ? text('userGroups.form.saveChanges', 'Save Changes')
              : text('userGroups.form.createUserGroup', 'Create User Group')}
          </Button>
        )}
      </Box>
    </Card.Root>
  )
}
